import { useCallback } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import LanguageSelectForm from "components/molecules/LanguageSelectForm";
import { useGTM } from "hooks/useGTM";
import { useBreakpoint } from "hooks/useBreakpoint";
import { useLanguage } from "hooks/useLanguage";
import { makeLangUrl } from "helpers/makeLangUrl";
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";
import { useCatalogsStore } from "stores/catalogs";
import { useLocalizationStore } from "stores/localization";
import { useQueryClient } from "@tanstack/react-query";
import { fetchLocalization } from "features/localization/fetchLocalization";
import i18next from "i18next";
import { setLocalStorageItem } from "utils/localStorage";

const LanguageSelectFormContainer = () => {
  const GTM = useGTM();
  const languages = useCatalogsStore((state) => state.languages);
  const { language, changeLanguage } = useLocalizationStore((state) => ({
    language: state.language,
    changeLanguage: state.changeLanguage,
  }));

  const queryClient = useQueryClient();
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const { path } = useLanguage();
  const methods = useForm();

  const handleLanguageChange = useCallback(
    async (isoCode: string) => {
      GTM.clickChangeLanguage(isoCode);

      // запомним прежний язык
      const prev = (
        useLocalizationStore.getState().language || "en"
      ).toLowerCase();
      setLocalStorageItem("lang_prev", prev);

      // 1) предзагрузка
      const qc = queryClient;
      const data =
        (qc.getQueryData(["localization", isoCode]) as any) ??
        (await qc.fetchQuery({
          queryKey: ["localization", isoCode],
          queryFn: () => fetchLocalization(isoCode),
          staleTime: 5 * 60 * 1000,
        }));

      // 2) кладем bundle в i18next (чтобы backend не сделал свой сетевой вызов)
      if (!i18next.hasResourceBundle(isoCode, "translation") && data) {
        i18next.addResourceBundle(isoCode, "translation", data, true, true);
      }

      // 3) переключаем язык (LS и стор обновятся внутри changeLanguage)
      changeLanguage(isoCode);

      // 4) обновляем URL
      navigate(
        {
          pathname: makeLangUrl(path, isoCode),
          ...(!isDesktop && {
            search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.sideMenu })}`,
          }),
        },
        { replace: true },
      );
    },
    [GTM, queryClient, navigate, path, isDesktop, changeLanguage],
  );

  if (!languages) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <LanguageSelectForm
        languages={languages}
        selectedLanguage={language || "en"}
        onChange={handleLanguageChange}
      />
    </FormProvider>
  );
};

export default LanguageSelectFormContainer;
